import { clsx } from "clsx";
import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import {
  ButtonSizes,
  ButtonVariant,
  getSingleIconClassNames,
  getVariantClassNames,
} from "./class-names";
import { BRANDING_VARS } from "../constants";
import { Icon, IconName } from "../icon";
import { Spinner } from "../spinner";

export { BRANDING_VARS };

export type ButtonProps = {
  variant?: ButtonVariant;
  text?: string;
  title?: string;
  leftIcon?: IconName | ReactNode;
  rightIcon?: IconName | ReactNode;
  dataTestId?: string;
  loading?: boolean;
  size?: ButtonSizes;
  rounded?: boolean;
  eventCaptureAttributes?: Record<string, string | boolean | undefined>;
  isPressed?: boolean;
  branded?: boolean;
} & Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | "type"
  | "className"
  | "disabled"
  | "onClick"
  | "onMouseDown"
  | "onTouchStart"
  | "name"
  | "value"
>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primaryDefault",
      className,
      text,
      leftIcon,
      rightIcon,
      dataTestId,
      loading,
      disabled,
      eventCaptureAttributes,
      size = "default",
      rounded,
      isPressed,
      branded,
      ...buttonProps
    },
    ref,
  ) => {
    const spinnerColors = {
      primaryDefault: "inverted",
      destructiveDefault: "primary",
      destructiveSubtle: "destructive",
      primarySubtle: "primary",
      secondaryDefault: "secondary",
      secondarySubtle: "secondary",
      highlight: "primary",
      invertedDefault: "inverted",
      invertedSubtle: "inverted",
      inlineDefault: "secondary",
      inlineSubtle: "secondary",
    } as const;

    const content = loading ? (
      <Spinner size="regular" color={spinnerColors[variant]} />
    ) : (
      <>
        {leftIcon && typeof leftIcon === "string" ? (
          <Icon name={leftIcon as IconName} />
        ) : (
          leftIcon
        )}
        {text && (
          <span className="text-ui font-inter font-semibold">{text}</span>
        )}
        {rightIcon && typeof rightIcon === "string" ? (
          <Icon name={rightIcon as IconName} />
        ) : (
          rightIcon
        )}
      </>
    );

    const isSingleIcon =
      loading ||
      (leftIcon && !rightIcon && !text) ||
      (!leftIcon && rightIcon && !text);

    return (
      <button
        className={twMerge(
          clsx(
            getVariantClassNames({
              loading,
              variant,
              size,
              rounded,
              isPressed,
              branded,
            }),
            isSingleIcon && getSingleIconClassNames(variant, size),
            className,
          ),
        )}
        ref={ref}
        type="button"
        data-testid={dataTestId}
        aria-pressed={isPressed}
        {...buttonProps}
        {...eventCaptureAttributes}
        disabled={disabled || loading}
      >
        {content}
      </button>
    );
  },
);
